/* eslint-disable @typescript-eslint/camelcase */
import React, { useEffect, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import Helmet from 'react-helmet';
import CabModifySearch from '../../components/CabModifySearch/CabModifySearch';
import FaqSection from '../../components/Faq/Faq';
import TopCabsRoutes from '../../components/TopCabsRoutes/TopCabsRoutes';
import Footer from '../../components/Footer/Footer';
import BreadCrumb from '../../components/BreadCrumb/BreadCrumb';
import PageHeader from '../../components/PageHeader/PageHeader';
import { trackAirportToCityRouteTemplateEvent } from '../../utils/omnitureTracking';
import DataLoaderError from '../../../server/errors/DataLoaderError';
import AppContextProvider from '../../AppContext';
import '../CityOperatorTemplate/CityOperatorTemplate.css';
import { EnvironmentVariables } from '../../types';
import { getAppEnv } from '../../../config/env';
import Overlay from '../../components/BPG/Overlay';
import { useKnowMoreDetails } from '../customHooks';
import CompactList from '../../components/CompactList/CompactList';
import AdsComponent from '../../components/adsComponent';
import { SEO_A2C_ROUTE_TEMPLATE } from '../../constants/templateConstants';
import ThingsToKnow from '../../components/ThingsToKnow/ThingsToKnow';
import { yeildToMainThread } from '../../scheduler';
import {
  detectUserActivity,
  fetchAndLogXdmEvent,
  generateRequestId,
  isMobile,
  useSearchWidgetHooks
} from '../../utils/commonUtils';
import ProductSchema from '../../utils/ProductSchema';
import { SCROLL } from '../../constants/miscellaneousConstants';
import { makeServiceDeskRequest } from '../../cabsAPIs';
import AssistModal from '../../components/AssistModal';
import CallUsModal from '../../components/AssistModal/callUsModal';
import InboundCallBox from '../../components/InboundCall/InboundCallBox';
import TripAssistant from '../TripAssistant';
const ASSISTED_FORM_DATA_LS = 'ASSISTED_FORM_DATA_LS';

const env = getAppEnv();
const { DATA_LOADER_URL } = env.raw as EnvironmentVariables;

const AirportToCityRoutePage = ({ data }) => {
  // TODO: use types
  const isMobileDevice = useMediaQuery({
    query: '(max-device-width: 768px)'
  });

  const [
    selectedKnowMoreDetailsForMobile,
    setSelectedKnowMoreDetailsForMobile
  ] = useKnowMoreDetails(null, isMobileDevice);
  const delayedKnowMoreDetailsForMobile = async val => {
    await yeildToMainThread(() => setSelectedKnowMoreDetailsForMobile(val));
  };

  const { travelFor, pickUpTime, dropTime } = useSearchWidgetHooks(
    SEO_A2C_ROUTE_TEMPLATE
  );
  const {
    topRoutesfromAirport,
    topRoutesToAirport,
    sourceCityData,
    sourceAirportData,
    destinationCityData,
    templateData,
    topRoutesInfoFromSourceToDestination,
    showQuickBookCabsButton,
    hrPackages,
    aggregateRating,
    interlinks,
    schemaMinFare,
    assistedFormOpenDelay = 10000,
    showTripAssistantButton = true,
    showTripAssistantButtonForDweb = true,
    showWhatsappWidget = true,
    whatsappWidgetDeeplink = 'https://wa.me/919951106499?text=Request%20ID%20MDRequest_id%20%0AHi,%20I%20am%20looking%20for%20a%20cab%20from%20Delhi%20to%20Agra',
    customerCareNumber = '01244781861',
    assistantWidgetText = 'You can Call Us or Chat via WhatsApp',
    showWhatsAppForAssistedForm = true,
    showCallUsWidget = false,
    showCallUsForAssistedForm = true,
    inboundCallText = 'Talk to agent (24x7)',
    inboundCallNumber = '0124 - 4781861',
    showInboundCallNumberInDesktop = true,
    showCallusModalMweb = false,
    callusModalOpenDelay = 10000,
    showInboundNumberOnAssistedForm = true,
    couponInfo = null,
    showLeaveIntentForm = false
  } = data;
  const { name: sourceCityName } = sourceCityData;

  const {
    airportTerminalName: sourceAirportName,
    code: sourceAirportCode
  } = sourceAirportData;
  const {
    name: destinationCityName,
    code: destinationCityCode
  } = destinationCityData;

  const [showAssistantIc, setShowAssistantIc] = useState(false);
  const [showWhatsAppIcon, setWhatsAppIcon] = useState(false);
  const [showCallUsIcon, setCallUsIcon] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const scrollPosition = useRef(0);
  const assistedFormLifeCycle = useRef(0); // 0:init,1:scroll down to threshold, 2: scroll up to threshold, and automated modal shown
  const [isOverlayVisible, setIsOverlayVisible] = useState(false);

  const toggleOverlay = (isVisible: boolean) => {
    setIsOverlayVisible(isVisible);
  };

  const userActionDone = () => {
    return window?.localStorage?.getItem('userActionDone') == 'true';
  };

  const setShowModalWithDelay = async val => {
    if (
      isMobile()
        ? showCallusModalMweb || showTripAssistantButton
        : showTripAssistantButtonForDweb
    ) {
      await yeildToMainThread(() =>
        requestAnimationFrame(() => setShowModal(val))
      );
    }
  };
  const showModalConsideringScrollEvents = () => {
    if (assistedFormLifeCycle.current != 2 && !userActionDone()) {
      assistedFormLifeCycle.current = 2;
      setShowModalWithDelay(true);
      const eventName =
        showCallusModalMweb && showCallUsForAssistedForm && isMobile()
          ? 'Cab_SEO_Assisted_Call_Us_Form_Shown'
          : 'Cab_SEO_Assisted_Form_Shown';
      trackAirportToCityRouteTemplateEvent(eventName);
    }
  };

  useEffect(() => {
    //
    const handleScroll = () => {
      scrollPosition.current = window.scrollY || window.pageYOffset;

      const scrollDownThreshold = isMobileDevice ? 500 : 900;
      const scrollUpThreshold = scrollDownThreshold - 100;

      if (
        scrollPosition.current > scrollDownThreshold &&
        assistedFormLifeCycle.current == 0
      ) {
        assistedFormLifeCycle.current = 1;
      } else if (
        scrollPosition.current < scrollUpThreshold &&
        assistedFormLifeCycle.current == 1
      ) {
        showModalConsideringScrollEvents();
      }
    };

    // If tripAssistantOpen is true in the query params then open form
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get('tripAssistantOpen') === 'true') {
      showModalConsideringScrollEvents();
    }

    // Attach the scroll event listener
    window.addEventListener(SCROLL, handleScroll);
    if (window?.localStorage) {
      localStorage.removeItem(ASSISTED_FORM_DATA_LS);
    }

    openFormDueToInactivity();
    if (showCallUsWidget && isMobile()) {
      setTimeout(() => {
        trackAirportToCityRouteTemplateEvent('Cab_SEO_Call_Us_Icon_Shown');
      }, 500);
    }

    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener(SCROLL, handleScroll);
    };
  }, []);

  const openFormDueToInactivity = () => {
    const timeDelay = showCallusModalMweb
      ? callusModalOpenDelay
      : assistedFormOpenDelay;
    setTimeout(() => {
      if (window?.localStorage && assistedFormLifeCycle.current != 2) {
        if (
          !userActionDone() &&
          isMobile() &&
          (showTripAssistantButton || showCallusModalMweb)
        ) {
          showModalConsideringScrollEvents();
        } else {
          // not show message should not be shown if modal is already shown
          const userAction = userActionDone();
          trackAirportToCityRouteTemplateEvent(
            `Cab_SEO_Assisted_Form_Not_Shown_userActionDone_${userAction}`
          );
        }
      }
    }, timeDelay);
  };

  useEffect(() => {
    window.addEventListener(SCROLL, listenToScroll);
    detectUserActivity();
    return () => window.removeEventListener(SCROLL, listenToScroll);
  }, []);

  useEffect(() => {
    const url =
      typeof window !== 'undefined'
        ? window.location.href
        : templateData?.sampleUrl || '';
    fetchAndLogXdmEvent(url);
  }, []);

  const listenToScroll = () => {
    const heightToShowFrom = 100;
    const winScroll =
      document.body.scrollTop || document.documentElement.scrollTop;
    if (winScroll > heightToShowFrom) {
      if (!(isMobileDevice && showModal)) {
        setShowAssistantIc(true);
        setWhatsAppIcon(true);
        setCallUsIcon(true);
      }
    } else {
      setShowAssistantIc(false);
      setWhatsAppIcon(false);
      setCallUsIcon(false);
    }
  };

  const fabInteraction = () => {
    return (
      <div
        className={` ${(() => {
          if (showTripAssistantButtonForDweb && showTripAssistantButton) {
            return '';
          } else if (showTripAssistantButton) {
            return 'mobileShow';
          } else if (showTripAssistantButtonForDweb) {
            return 'desktopShow';
          }
          return '';
        })()}`}
      >
        <div
          onClick={() => {
            trackAirportToCityRouteTemplateEvent(
              `${
                showModal
                  ? 'Cab_SEO_Assisted_Form_Closed'
                  : 'Cab_SEO_Assisted_Form_CTA_Click'
              }`
            );
            setShowModalWithDelay(!showModal);
            assistedFormLifeCycle.current = 2; // keeping this 2 as for both the cases Closed and Clicked, Assisted form will be shown
          }}
          className={`fabInteraction ${
            showModal && isMobileDevice
              ? 'showCrossIc'
              : showAssistantIc
              ? 'showIc'
              : ''
          }`}
        >
          <div className={`icContainer ${showModal ? 'showCrossIc' : ''}`} />
          {true && <span>Trip Assistant</span>}
        </div>
      </div>
    );
  };

  const callUsForm = () => {
    return (
      <div
        className={` ${(() => {
          if (showCallusModalMweb) {
            return '';
          }
          return '';
        })()}`}
      >
        <div
          onClick={() => {
            trackAirportToCityRouteTemplateEvent(
              `${
                showModal
                  ? 'Cab_SEO_Assisted_Call_Us_Form_Closed'
                  : 'Cab_SEO_Assisted_Form_CTA_Click'
              }`
            );
            setShowModalWithDelay(!showModal);
            assistedFormLifeCycle.current = 2; // keeping this 2 as for both the cases Closed and Clicked, Assisted form will be shown
          }}
          className={`${
            showModal && isMobileDevice
              ? 'fabInteraction showCrossIc'
              : showAssistantIc
              ? 'showIc'
              : ''
          }`}
        >
          <div className={`icContainer ${showModal ? 'showCrossIc' : ''}`} />
        </div>
      </div>
    );
  };

  const onWhatsAppIconClick = () => {
    const requestId = generateRequestId();
    const pageName = 'seo_a2c_page';
    makeServiceDeskRequest(requestId, pageName);
    const whatsAppChatLinkWithRequestId = whatsappWidgetDeeplink
      ? whatsappWidgetDeeplink.replace('MDRequest_id', requestId)
      : '';
    if (whatsAppChatLinkWithRequestId)
      window.location.href = whatsAppChatLinkWithRequestId;
  };

  const onCallUsIconClick = () => {
    window.open(`tel:${customerCareNumber}`, '_blank');
  };

  return (
    <AppContextProvider
      omniUrlArgs={`source=${SEO_A2C_ROUTE_TEMPLATE}`}
      trackTemplateEvent={trackAirportToCityRouteTemplateEvent}
      selectedKnowMoreDetailsForMobile={selectedKnowMoreDetailsForMobile}
      setSelectedKnowMoreDetailsForMobile={delayedKnowMoreDetailsForMobile}
    >
      <div>
        {isMobile() && showWhatsappWidget && (
          <div
            className={`${!showModal ? 'whatsApp' : ''} ${
              showModal ? '' : showWhatsAppIcon ? 'showWhatsAppIcon' : ''
            }`}
            onClick={() => {
              onWhatsAppIconClick();
              trackAirportToCityRouteTemplateEvent(
                'Cab_SEO_WhatsApp_Assisted_Icon_Clicked'
              );
            }}
          >
            <div className={`${!showModal ? 'whatsaAppIconContainer' : ''}`} />
            {true && !showModal && <span>Chat with us</span>}
          </div>
        )}
      </div>
      <div>
        {isMobile() && showCallUsWidget && customerCareNumber && (
          <div
            className={`${!showModal ? 'callUs' : ''} ${
              showModal ? '' : showCallUsIcon ? 'showCallUsIcon' : ''
            }`}
            onClick={() => {
              onCallUsIconClick();
              trackAirportToCityRouteTemplateEvent(
                'Cab_SEO_Call_Us_Icon_Clicked'
              );
            }}
          >
            <div className={`${!showModal ? 'callUsIconContainer' : ''}`} />
            {true && !showModal && <span>Call us now</span>}
          </div>
        )}
      </div>
      {isMobile() && showTripAssistantButton ? fabInteraction() : <div></div>}
      {showCallusModalMweb && callUsForm()}
      {(showTripAssistantButtonForDweb || showLeaveIntentForm) &&
      !isMobile() ? (
        <div className="tripAssistant">
          <TripAssistant
            trackEvent={trackAirportToCityRouteTemplateEvent}
            source={sourceAirportName}
            autoOpenDelay={assistedFormOpenDelay}
            couponData={couponInfo}
            showTripAssistant={showTripAssistantButtonForDweb}
            showLeaveIntentForm={showLeaveIntentForm}
          />
        </div>
      ) : (
        <div className="tripAssistant"></div>
      )}
      {showModal && isMobile() && showTripAssistantButton && (
        <AssistModal
          sourceCity={sourceAirportName}
          setShowModal={val => setShowModalWithDelay(val)}
          trackTemplateEvent={trackAirportToCityRouteTemplateEvent}
          showWhatsAppForAssistedForm={showWhatsAppForAssistedForm}
          showCallUsForAssistedForm={showCallUsForAssistedForm}
          customerCareNumber={customerCareNumber}
          assistantWidgetText={assistantWidgetText}
          onCallUsIconClick={onCallUsIconClick}
          onWhatsAppIconClick={onWhatsAppIconClick}
          showInboundNumberOnAssistedForm={showInboundNumberOnAssistedForm}
          inboundCallNumber={inboundCallNumber}
          inboundCallText={inboundCallText}
          destinationCity={destinationCityName}
        />
      )}
      {showModal && isMobile() && showCallusModalMweb && (
        <CallUsModal
          setShowModal={val => setShowModalWithDelay(val)}
          trackTemplateEvent={trackAirportToCityRouteTemplateEvent}
          showWhatsAppForAssistedForm={showWhatsAppForAssistedForm}
          showCallUsForAssistedForm={showCallUsForAssistedForm}
          customerCareNumber={customerCareNumber}
          assistantWidgetText={assistantWidgetText}
          onCallUsIconClick={onCallUsIconClick}
          onWhatsAppIconClick={onWhatsAppIconClick}
          showModal={showModal}
        />
      )}
      <div
        className={`cabSeoDtPage ${
          selectedKnowMoreDetailsForMobile ? 'expanded' : ''
        }`}
      >
        <Helmet>
          <title>{templateData.metaTitle}</title>
          <meta name="description" content={templateData.metaDescription} />
          <meta name="keywords" content={templateData.metaKeywords} />
          <link rel="canonical" href={templateData.sampleUrl} />
        </Helmet>
        {aggregateRating && (
          <ProductSchema
            data={aggregateRating}
            templateData={templateData}
            schemaMinFare={schemaMinFare}
            source={sourceAirportName}
            dest={destinationCityName}
          />
        )}
        <div className={`desktopShow makeSticky`}>
          <CabModifySearch
            pickupTime={pickUpTime}
            defaultTripType={travelFor}
            returnTime={dropTime}
            returnCloseFlagDefault={false}
            hrPackages={hrPackages}
            sourceCityCode={sourceAirportCode}
            sourceCityName={sourceAirportName}
            isMobileDevice={false}
            title={`${sourceAirportName} to ${destinationCityName} Cabs`}
          />
        </div>

        <div className={`domChanger mobileShow makeSticky`}>
          <CabModifySearch
            isOverlayVisible={isOverlayVisible}
            overlayToggle={toggleOverlay}
            pickupTime={pickUpTime}
            defaultTripType={travelFor}
            returnTime={dropTime}
            returnCloseFlagDefault={false}
            hrPackages={hrPackages}
            sourceCityCode={sourceAirportCode}
            // destinationCityCode={destinationCityCode}
            sourceCityName={sourceAirportName}
            // destinationCityName={destinationCityName}
            isMobileDevice={true}
            title={`${sourceAirportName} to ${destinationCityName} Cabs`}
            showQuickBookCabsButton={showQuickBookCabsButton}
          />
        </div>
        <main className="container">
          <div>
            <div className="makeFlex">
              <BreadCrumb breadcrumb={templateData.breadcrumb} />
              {showInboundCallNumberInDesktop ? (
                <InboundCallBox
                  inboundCallText={inboundCallText}
                  inboundCallNumber={inboundCallNumber}
                />
              ) : (
                <div></div>
              )}
            </div>
            <div>
              <PageHeader
                title={templateData.h1Title}
                extraInfo={templateData.metaDescription}
                isMobileDevice={isMobileDevice}
                url={templateData.sampleUrl}
              />
            </div>
            <div>
              <div>
                <AdsComponent
                  template={'A2C_Route'}
                  placedAt={'top'}
                  isMobileDevice={isMobileDevice}
                  desktopAdConfig={{
                    position: templateData.bannerDesktop
                      ? templateData.bannerDesktop
                      : 'none',
                    class: 'desktopShow appendBottom20 adsLayout',
                    style: {}
                  }}
                  mobileAdConfig={{
                    position: templateData.bannerMobile
                      ? templateData.bannerMobile
                      : 'none',
                    class: 'mobileShow appendBottom20 adsLayout',
                    style: { marginTop: null }
                  }}
                />
              </div>
            </div>
            {/* {topRoutesInfoFromSourceToDestination.cabsList.length > 0 && (
              <section>
                {isMobileDevice ? (
                  <>
                    <h2
                      style={{ marginBottom: '0px' }}
                    >{`${sourceAirportName} to ${destinationCityName} one way distance  ${topRoutesInfoFromSourceToDestination.oneWayDistance} kms`}</h2>
                    <h2>{`One way duration ${topRoutesInfoFromSourceToDestination.oneWayDurationInHrMins}`}</h2>
                  </>
                ) : (
                  <h2>{`${sourceAirportName} to ${destinationCityName} one way distance ${topRoutesInfoFromSourceToDestination.oneWayDistance} kms | One way duration ${topRoutesInfoFromSourceToDestination.oneWayDurationInHrMins}`}</h2>
                )}
                <RouteCabs
                  data={topRoutesInfoFromSourceToDestination?.cabsList}
                  seoUrl={topRoutesInfoFromSourceToDestination.seoUrl}
                  sourceCityName={sourceAirportName}
                  destinationCityName={destinationCityName}
                />
              </section>
            )} */}

            {topRoutesfromAirport.cabTypes.length > 0 ? (
              <TopCabsRoutes
                h2={`Top Cab Routes from ${sourceAirportName}`}
                cityName={sourceAirportName}
                routeType="From"
                data={topRoutesfromAirport}
                enableAnchorTags={true}
                reduceCssCalculations={false}
              />
            ) : null}
            {topRoutesToAirport.cabTypes.length > 0 ? (
              <TopCabsRoutes
                h2={`Top Cab Routes to ${sourceAirportName}`}
                cityName={sourceAirportName}
                routeType="To"
                data={topRoutesToAirport}
                enableAnchorTags={true}
              />
            ) : null}
            {templateData.descriptionInHtml &&
            topRoutesInfoFromSourceToDestination &&
            topRoutesInfoFromSourceToDestination.cabsList &&
            topRoutesInfoFromSourceToDestination.cabsList.length > 0 ? (
              <section id="descriptionInHtml" className="visibilityAuto">
                <ThingsToKnow html={templateData.descriptionInHtml} />
              </section>
            ) : null}
            {templateData.faq.length > 0 && (
              <section className="visibilityAuto">
                <h2>{`Frequently Asked Questions for ${sourceAirportName} to ${destinationCityName} Cab Booking`}</h2>
                <FaqSection
                  trackTemplateEvent={trackAirportToCityRouteTemplateEvent}
                  data={templateData.faq}
                  fromCityCode={sourceAirportCode}
                  toCityCode={destinationCityCode}
                  pageUrl={templateData.sampleUrl}
                />
              </section>
            )}
            <div
              style={{ flexWrap: 'wrap' }}
              className={`cabsSeoWrap__Details makeFlex visibilityAuto`}
            >
              {interlinks &&
                interlinks.map(({ title, links }) => {
                  return (
                    <CompactList
                      titlePrefix={title}
                      titleSuffix={``}
                      list={links}
                      type={'bullets'}
                      className={'rightMargin'}
                    >
                      {(item: any, idx, style) => (
                        <li
                          key={`${item.text}_${idx}`}
                          className={`${
                            idx ? `paddingT10 paddingB10` : `paddingB10`
                          }`}
                        >
                          {item.url ? (
                            <a
                              className={`linkBoxFont `}
                              onClick={() => {
                                //TO DO: add omniture events from Dataloader
                              }}
                              href={item.url}
                            >
                              {item.text}
                            </a>
                          ) : (
                            <span>{item.text}</span>
                          )}
                        </li>
                      )}
                    </CompactList>
                  );
                })}
            </div>
          </div>
        </main>
        {selectedKnowMoreDetailsForMobile ? (
          <Overlay
            cancelButton={() => delayedKnowMoreDetailsForMobile(null)}
            knowMoreDetails={selectedKnowMoreDetailsForMobile}
          />
        ) : null}
        <Footer template={templateData} />
        <div
          className="blackOverlay"
          onClick={() => {
            delayedKnowMoreDetailsForMobile(null);
          }}
        ></div>
        {isOverlayVisible && (
          <div
            onClick={() => {
              toggleOverlay(false);
            }}
            className="searchWidgetModal mobileShow"
          ></div>
        )}
      </div>
    </AppContextProvider>
  );
};

AirportToCityRoutePage.fetchData = async (_, match, urlObj) => {
  const {
    params: { a2cRouteId }
  } = match;
  const url = `${DATA_LOADER_URL}/api/a2cRouteTemplateData/${a2cRouteId}?${urlObj.query}`;
  let responseData;
  const response = await fetch(url);
  const textResponse = await response.text();
  try {
    responseData = JSON.parse(textResponse);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('url', url);
    throw new Error('JSON parse exception: ' + textResponse);
  }
  if (responseData.status !== 'SUCCESS') {
    // eslint-disable-next-line no-console
    console.error('url', url);
    throw new DataLoaderError(
      `Unable to fetch city details of ${a2cRouteId}`,
      responseData.error
    );
  }
  return responseData;
};
export default AirportToCityRoutePage;
